import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { from, of, timer } from "rxjs";
import {
  catchError,
  filter,
  ignoreElements,
  map,
  retry,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { ConfigService } from "../../core/config/config.service";
import { WebsocketFacade } from "../../core/websocket/websocket.facade";
import { SocketActions } from "./socket.actions";
import { getAccessToken } from "../auth/auth.selectors";

@Injectable()
export class SocketEffects {
  private readonly actions$ = inject(Actions);
  private readonly websocketFacade = inject(WebsocketFacade);
  private readonly configService = inject(ConfigService);
  private readonly store = inject(Store);

  readonly connect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SocketActions.connect),
      filter(() => this.configService.websocketEnabled()),
      withLatestFrom(this.store.select(getAccessToken)),
      switchMap(([, token]) => {
        const config = this.configService.config();

        if (!config?.socketUrl || !token) {
          return of(
            SocketActions.error({
              error: "Socket config or token is missing",
            }),
          );
        }


        if (this.websocketFacade.isConnected()) {
          console.log("Socket already connected");
          return of(SocketActions.connected());
        } else {
          // We are reconnecting here. We need to send a message
          this.store.dispatch(SocketActions.messageReceived({ message: {
            message: "reconnecting",
            sentAt: new Date().toISOString()
           } }));
          console.warn("Socket disconnected. Reconecting...");
        }


        return from(
          this.websocketFacade.connect({
            url: config.socketUrl,
            token,
          }),
        ).pipe(
          ignoreElements(),
          retry({
            count: 5, // máximo 5 reintentos
            delay: (_, attempt) => {
              // Backoff exponencial: 2s, 4s, 8s, 16s, 32s
              const ms = Math.min(1000 * Math.pow(2, attempt), 30000);
              console.warn(`Socket disconnected. Retry ${attempt} in ${ms}ms`);
              return timer(ms);
            },
            resetOnSuccess: true,
          }),
          catchError((error) =>
            of(
              SocketActions.error({
                error: error?.message ?? "Socket connection error",
              }),
            ),
          ),
        );
      }),
    ),
  );

  readonly subscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SocketActions.subscribe),
      switchMap(({ channel }) =>
        from(this.websocketFacade.subscribe(channel)).pipe(
          ignoreElements(),
          catchError((error) =>
            of(
              SocketActions.error({
                error: error?.message ?? `Socket subscribe error: ${channel}`,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly unsubscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SocketActions.unsubscribe),
      switchMap(({ channel }) =>
        from(this.websocketFacade.unsubscribe(channel)).pipe(
          ignoreElements(),
          catchError((error) =>
            of(
              SocketActions.error({
                error: error?.message ?? `Socket unsubscribe error: ${channel}`,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly disconnect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SocketActions.disconnect),
      switchMap(() =>
        from(this.websocketFacade.disconnect()).pipe(
          ignoreElements(),
          catchError((error) =>
            of(
              SocketActions.error({
                error: error?.message ?? "Socket disconnect error",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly opened$ = createEffect(() =>
    this.websocketFacade.opened$.pipe(map(() => SocketActions.connected())),
  );

  readonly closed$ = createEffect(() =>
    this.websocketFacade.closed$.pipe(map(() => SocketActions.disconnected())),
  );

  readonly reconnecting$ = createEffect(() =>
    this.websocketFacade.reconnecting$.pipe(
      map(() => SocketActions.reconnecting()),
    ),
  );

  readonly errors$ = createEffect(() =>
    this.websocketFacade.errors$.pipe(
      map((error) => SocketActions.error({ error })),
    ),
  );

  readonly messages$ = createEffect(() =>
    this.websocketFacade.messages$.pipe(
      map((message) => SocketActions.messageReceived({ message })),
    ),
  );
}
